import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Table } from "../../components/ui/Table";
import { Pagination } from "../../components/ui/Pagination";
import { formatFecha } from "../../lib/dates";
import { InformeDireccionPanel } from "./InformeDireccionPanel";

interface EconomicoResumen {
  total_seguros: number;
  total_pilotos_facturados: number;
  importe_prima_bruta: number;
  total_recargo: number;
  importe_prima_neta: number;
  total_comisiones: number;
  total_gastos_gestion: number;
  total_recaudado: number;
}

interface EconomicoDetalleRow {
  seguro_id: number;
  fecha_pago: string;
  fecha_efecto?: string;
  cliente: string;
  modalidad: string;
  evento: string | null;
  num_pilotos: number;
  importe_prima: number;
  recargo: number;
  prima_neta: number;
  comision: number;
  gastos_gestion: number;
  importe_total: number;
  metodo_pago: string;
}

interface ResumenMensual {
  cliente: { razon_social: string; cif: string };
  periodo: { mes: number; anio: number };
  seguros: Array<{ id: number; eventoReferencia?: string; createdAt?: string; paidAt?: string | null; importeTotal?: number; modalidad_nombre?: string; numPilotos?: number }>;
  totales: { importe_prima: number; gastos_gestion: number; importe_total: number };
  recibo_mensual: unknown;
}

const now = new Date();
const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);

const ESTADO_TRANSFERENCIA_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "pendiente", label: "Pendiente" },
  { value: "pagado", label: "Pagado" },
  { value: "cancelado", label: "Cancelado" },
];

function formatMoneda(value: number) {
  return (
    <span className="whitespace-nowrap text-right block">
      {Number(value).toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}{" "}
      €
    </span>
  );
}

function formatMonedaText(value: number) {
  return `${Number(value).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

export function InformesPage() {
  const [tab, setTab] = useState<"direccion" | "economico" | "transferencias">("direccion");
  const [filters, setFilters] = useState({ fecha_desde: firstDay, fecha_hasta: lastDay, cliente_id: "", modalidad_id: "" });
  const [resumen, setResumen] = useState<EconomicoResumen | null>(null);
  const [detalle, setDetalle] = useState<EconomicoDetalleRow[]>([]);
  const [paginacion, setPaginacion] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState<{ id: number; razonSocial?: string; metodoPago?: string }[]>([]);
  const [modalidades, setModalidades] = useState<{ id: number; nombre: string }[]>([]);

  const [transfCliente, setTransfCliente] = useState("");
  const [transfMes, setTransfMes] = useState(String(now.getMonth() + 1));
  const [transfAnio, setTransfAnio] = useState(String(now.getFullYear()));
  const [estadoTransferencia, setEstadoTransferencia] = useState<
    "" | "pendiente" | "pagado" | "cancelado"
  >("");
  const [resumenMensual, setResumenMensual] = useState<ResumenMensual | null>(null);
  const [transfLoading, setTransfLoading] = useState(false);
  const [transfActionLoading, setTransfActionLoading] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const fetchEconomico = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: "20" };
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      if (filters.cliente_id) params.cliente_id = filters.cliente_id;
      if (filters.modalidad_id) params.modalidad_id = filters.modalidad_id;
      const res = await api.get<{ resumen: EconomicoResumen; detalle: EconomicoDetalleRow[]; paginacion: { total: number; page: number; totalPages: number } }>("/admin/informes/economico", { params });
      setResumen(res.data.resumen);
      setDetalle(res.data.detalle ?? []);
      setPaginacion(res.data.paginacion ?? { page: 1, totalPages: 1, total: 0 });
    } catch {
      setResumen(null);
      setDetalle([]);
    } finally {
      setLoading(false);
    }
  }, [filters.fecha_desde, filters.fecha_hasta, filters.cliente_id, filters.modalidad_id]);

  useEffect(() => {
    if (tab === "economico") fetchEconomico(paginacion.page);
  }, [tab, fetchEconomico, paginacion.page]);

  const fetchClientes = useCallback(async () => {
    try {
      const res = await api.get<{ data: { id: number; razonSocial?: string; metodoPago?: string }[] }>("/admin/clientes", { params: { limit: 300 } });
      setClientes(res.data?.data ?? []);
    } catch {
      setClientes([]);
    }
  }, []);

  const fetchModalidades = useCallback(async () => {
    try {
      const res = await api.get<{ id: number; nombre: string }[]>("/admin/modalidades", { params: { incluir_inactivas: "true" } });
      setModalidades(Array.isArray(res.data) ? res.data : []);
    } catch {
      setModalidades([]);
    }
  }, []);

  useEffect(() => {
    fetchClientes();
    fetchModalidades();
  }, [fetchClientes, fetchModalidades]);

  const clientesTransferencia = clientes.filter((c) => c.metodoPago === "transferencia");

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      setExportError(null);
      const params = new URLSearchParams();
      if (filters.fecha_desde) params.set("fecha_desde", filters.fecha_desde);
      if (filters.fecha_hasta) params.set("fecha_hasta", filters.fecha_hasta);
      if (filters.cliente_id) params.set("cliente_id", filters.cliente_id);
      if (filters.modalidad_id) params.set("modalidad_id", filters.modalidad_id);

      const response = await api.get(`/admin/informes/economico/export?${params.toString()}`, {
        responseType: "blob",
      });

      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `informe-economico-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status !== 401) {
        setExportError("Error al exportar el Excel. Inténtalo de nuevo.");
      }
      console.error("Error exportando Excel:", err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleVerResumenMensual = async () => {
    if (!transfCliente || !transfMes || !transfAnio) return;
    setTransfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (estadoTransferencia) params.estado = estadoTransferencia;
      const res = await api.get<ResumenMensual>(
        `/admin/informes/resumen-mensual/${transfCliente}/${transfAnio}/${transfMes}`,
        { params }
      );
      setResumenMensual(res.data);
    } catch {
      setResumenMensual(null);
    } finally {
      setTransfLoading(false);
    }
  };

  const handleMarcarPagados = async () => {
    if (!transfCliente || !transfMes || !transfAnio) return;
    setTransfActionLoading("marcar");
    try {
      await api.post(`/admin/informes/marcar-transferencias-pagadas/${transfCliente}/${transfAnio}/${transfMes}`);
      await handleVerResumenMensual();
    } finally {
      setTransfActionLoading(null);
    }
  };

  const handleGenerarReciboMensual = async () => {
    if (!transfCliente || !transfMes || !transfAnio) return;
    setTransfActionLoading("recibo");
    try {
      await api.post(`/admin/informes/resumen-mensual/${transfCliente}/${transfAnio}/${transfMes}/generar-recibo`);
      await handleVerResumenMensual();
    } finally {
      setTransfActionLoading(null);
    }
  };

  const economicoColumns = [
    { key: "fecha_pago", header: "Fecha pago", render: (r: EconomicoDetalleRow) => formatFecha(r.fecha_pago) },
    { key: "fecha_efecto", header: "Fecha efecto", render: (r: EconomicoDetalleRow) => formatFecha(r.fecha_efecto) || "-" },
    { key: "cliente", header: "Cliente", render: (r: EconomicoDetalleRow) => r.cliente },
    { key: "modalidad", header: "Modalidad", render: (r: EconomicoDetalleRow) => r.modalidad },
    { key: "evento", header: "Paquete", render: (r: EconomicoDetalleRow) => r.evento?.trim() || "Sin paquete" },
    { key: "num_pilotos", header: "Pilotos", render: (r: EconomicoDetalleRow) => r.num_pilotos },
    { key: "importe_prima", header: "Prima", render: (r: EconomicoDetalleRow) => formatMoneda(r.importe_prima) },
    { key: "recargo", header: "Recargo", render: (r: EconomicoDetalleRow) => formatMoneda(r.recargo) },
    { key: "prima_neta", header: "Prima neta", render: (r: EconomicoDetalleRow) => formatMoneda(r.prima_neta) },
    { key: "comision", header: "Comisión", render: (r: EconomicoDetalleRow) => formatMoneda(r.comision) },
    { key: "gastos_gestion", header: "Gastos gest.", render: (r: EconomicoDetalleRow) => formatMoneda(r.gastos_gestion) },
    { key: "importe_total", header: "Total", render: (r: EconomicoDetalleRow) => formatMoneda(r.importe_total) },
    { key: "metodo_pago", header: "Método", render: (r: EconomicoDetalleRow) => r.metodo_pago ?? "-" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#2C2C2C]">Informes</h1>

      <div className="mb-4 flex max-w-full overflow-x-auto border-b">
        <button
          type="button"
          onClick={() => setTab("direccion")}
          className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === "direccion"
              ? "border-[#1A3AB8] text-[#1A3AB8]"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Informe de dirección
        </button>
        <button
          type="button"
          onClick={() => setTab("economico")}
          className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === "economico"
              ? "border-[#1A3AB8] text-[#1A3AB8]"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Informe económico
        </button>
        <button
          type="button"
          onClick={() => setTab("transferencias")}
          className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            tab === "transferencias"
              ? "border-[#1A3AB8] text-[#1A3AB8]"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Transferencias pendientes
        </button>
      </div>

      {tab === "direccion" && <InformeDireccionPanel />}

      {tab === "economico" && (
        <>
          <div className="space-y-3 mb-6">
            <div className="flex flex-wrap items-end gap-3">
              <Input
                type="date"
                label="Desde"
                value={filters.fecha_desde}
                onChange={(e) => setFilters((f) => ({ ...f, fecha_desde: e.target.value }))}
                className="w-36 min-w-[140px]"
              />
              <Input
                type="date"
                label="Hasta"
                value={filters.fecha_hasta}
                onChange={(e) => setFilters((f) => ({ ...f, fecha_hasta: e.target.value }))}
                className="w-36 min-w-[140px]"
              />
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <Select
                label="Cliente"
                options={[
                  { value: "", label: "Todos" },
                  ...clientes.map((c) => ({ value: String(c.id), label: c.razonSocial ?? "" })),
                ]}
                value={filters.cliente_id}
                onChange={(e) => setFilters((f) => ({ ...f, cliente_id: e.target.value }))}
                className="w-48"
              />
              <Select
                label="Modalidad"
                options={[
                  { value: "", label: "Todas" },
                  ...modalidades.map((m) => ({ value: String(m.id), label: m.nombre })),
                ]}
                value={filters.modalidad_id}
                onChange={(e) => setFilters((f) => ({ ...f, modalidad_id: e.target.value }))}
                className="w-40"
              />
              <Button onClick={() => fetchEconomico(1)}>Aplicar filtros</Button>
              <Button variant="secondary" loading={exportLoading} onClick={handleExportExcel}>
                Exportar Excel
              </Button>
            </div>
          </div>
          {exportError && <p className="text-sm text-red-600">{exportError}</p>}

          {resumen && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-8">
              <div className="bg-white rounded-lg border border-gray-200 p-4"><p className="text-xs text-gray-500">Total seguros</p><p className="text-lg font-semibold">{resumen.total_seguros}</p></div>
              <div className="bg-white rounded-lg border border-gray-200 p-4"><p className="text-xs text-gray-500">Pilotos facturados</p><p className="text-lg font-semibold">{resumen.total_pilotos_facturados}</p></div>
              <div className="bg-white rounded-lg border border-gray-200 p-4"><p className="text-xs text-gray-500">Prima bruta</p><p className="text-lg font-semibold whitespace-nowrap">{formatMonedaText(resumen.importe_prima_bruta)}</p></div>
              <div className="bg-white rounded-lg border border-gray-200 p-4"><p className="text-xs text-gray-500">Recargo</p><p className="text-lg font-semibold whitespace-nowrap">{formatMonedaText(resumen.total_recargo)}</p></div>
              <div className="bg-white rounded-lg border border-gray-200 p-4"><p className="text-xs text-gray-500">Prima neta</p><p className="text-lg font-semibold whitespace-nowrap">{formatMonedaText(resumen.importe_prima_neta)}</p></div>
              <div className="bg-white rounded-lg border border-gray-200 p-4"><p className="text-xs text-gray-500">Comisiones CC</p><p className="text-lg font-semibold whitespace-nowrap">{formatMonedaText(resumen.total_comisiones)}</p></div>
              <div className="bg-white rounded-lg border border-gray-200 p-4"><p className="text-xs text-gray-500">Gastos gestión</p><p className="text-lg font-semibold whitespace-nowrap">{formatMonedaText(resumen.total_gastos_gestion)}</p></div>
              <div className="bg-white rounded-lg border border-gray-200 p-4"><p className="text-xs text-gray-500">Total recaudado</p><p className="text-lg font-semibold text-[#1A3AB8] whitespace-nowrap">{formatMonedaText(resumen.total_recaudado)}</p></div>
            </div>
          )}

          <Table<EconomicoDetalleRow> columns={economicoColumns} data={detalle} loading={loading} emptyMessage="No hay datos" />
          <Pagination page={paginacion.page} totalPages={paginacion.totalPages} onPageChange={(p) => { setPaginacion((prev) => ({ ...prev, page: p })); fetchEconomico(p); }} />
        </>
      )}

      {tab === "transferencias" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <Select label="Cliente (transferencia)" options={[{ value: "", label: "Seleccionar" }, ...clientesTransferencia.map((c) => ({ value: String(c.id), label: c.razonSocial ?? "" }))]} value={transfCliente} onChange={(e) => setTransfCliente(e.target.value)} className="w-56" />
            <Select label="Mes" options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => ({ value: String(m), label: String(m) }))} value={transfMes} onChange={(e) => setTransfMes(e.target.value)} className="w-20" />
            <Select label="Año" options={[now.getFullYear(), now.getFullYear() - 1].map((y) => ({ value: String(y), label: String(y) }))} value={transfAnio} onChange={(e) => setTransfAnio(e.target.value)} className="w-24" />
            <Select
              label="Estado"
              options={ESTADO_TRANSFERENCIA_OPTIONS}
              value={estadoTransferencia}
              onChange={(e) =>
                setEstadoTransferencia(
                  e.target.value as "" | "pendiente" | "pagado" | "cancelado"
                )
              }
              className="w-36"
            />
            <Button loading={transfLoading} disabled={!transfCliente} onClick={handleVerResumenMensual}>Ver resumen mensual</Button>
            <Button variant="secondary" loading={transfActionLoading === "marcar"} disabled={!transfCliente} onClick={handleMarcarPagados}>Marcar todos como pagados</Button>
            <Button variant="secondary" loading={transfActionLoading === "recibo"} disabled={!transfCliente} onClick={handleGenerarReciboMensual}>Generar recibo mensual</Button>
          </div>
          {resumenMensual && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <p><strong>{resumenMensual.cliente?.razon_social}</strong> — {resumenMensual.periodo.mes}/{resumenMensual.periodo.anio}</p>
              <div className="overflow-x-auto"><table className="min-w-full text-sm">
                <thead><tr className="border-b"><th className="text-left py-2">Paquete</th><th className="text-left py-2">Modalidad</th><th className="text-left py-2">Fecha</th><th className="text-right py-2">Pilotos</th><th className="text-right py-2">Total</th></tr></thead>
                <tbody>
                  {resumenMensual.seguros?.map((s) => (
                    <tr key={s.id} className="border-b">
                      <td className="py-2">{s.eventoReferencia?.trim() || "Sin paquete"}</td>
                      <td className="py-2">{s.modalidad_nombre ?? "-"}</td>
                      <td className="py-2">{formatFecha(s.paidAt ?? s.createdAt)}</td>
                      <td className="py-2 text-right">{s.numPilotos ?? 0}</td>
                      <td className="py-2 text-right whitespace-nowrap">{formatMonedaText(Number(s.importeTotal ?? 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
              <p className="font-semibold whitespace-nowrap">
                Total: {formatMonedaText(resumenMensual.totales?.importe_total ?? 0)}
              </p>
              {resumenMensual.recibo_mensual ? <p className="text-green-600 text-sm">Recibo mensual generado</p> : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
